import React from "react";
import { CheckCheck, Loader2, PieChart, RefreshCw, TriangleAlert, User } from "lucide-react";
import { TraitConfig } from "../data";
import { useSampleProfiles } from "../api";
import { MockCustomerProfileSummary, formatProfileTimestamp } from "../../profile-explorer/api";
import { SectionHeader, ghostBtn, primaryBtn } from "./ui";

// The mock API has no "preview this exact config" endpoint, so there's no
// real per-trait value to show for an arbitrary trait name — this maps a
// few common built-in names to fields the profile summary actually has
// (balance/risk/engagement) and otherwise falls back to the form's own
// first value, rather than fabricating one.
const sampleValueFor = (c: MockCustomerProfileSummary, traitName: string, fallback: string): string => {
  const key = traitName.toLowerCase();
  if (key.includes("balance")) return c.balance_tier;
  if (key.includes("risk") || key.includes("churn")) return c.risk_level;
  if (key.includes("engagement")) return String(c.engagement_score);
  return fallback;
};

const Stat: React.FC<{ icon: React.ReactNode; label: string; value: string; note: string }> = ({ icon, label, value, note }) => (
  <div className="rounded-xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm p-4">
    <p className="flex items-center gap-2 text-lg text-gray-900 dark:text-gray-100">
      {icon} {label}
    </p>
    <p className="text-2xl text-gray-900 dark:text-gray-100 mt-3">{value}</p>
    <p className="text-sm text-gray-700 dark:text-gray-300 mt-3">{note}</p>
  </div>
);

// Coverage/sync/validation stats have no real per-trait computation in the
// mock API, so they stay demo estimates — clearly labeled as such. The
// sample table's 3 people are real synthetic profiles from the same mock
// dataset the Profile Explorer uses (GET /traits/customers?per_page=3),
// borrowed just for a realistic name/ID to preview against.
const StepPreview: React.FC<{ config: TraitConfig; onBack: () => void; onSave: () => void }> = ({ config, onBack, onSave }) => {
  const fallbackValue = config.dataType === "Enum" ? config.enumValues[0] || "—" : "Sample";
  const { data: rows, loading: customersLoading } = useSampleProfiles(3);

  return (
    <div>
      <SectionHeader title={`Preview: ${config.name || "trait"}`} subtitle="Review sample data and validate the trait before saving" />

      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] uppercase tracking-wide font-semibold bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded">
          Demo estimate
        </span>
        <p className="text-xs text-gray-500 dark:text-gray-400">The traits API doesn't compute live coverage for a trait you're still configuring.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <Stat icon={<PieChart size={22} className="text-indigo-700" />} label="Trait Coverage" value="98%" note="54k out of 60k customers have value" />
        <Stat icon={<RefreshCw size={22} className="text-indigo-700" />} label="Trait Synced" value="4min ago" note="Last Updated" />
        <Stat icon={<CheckCheck size={22} className="text-green-600" />} label="Validation" value="Healthy" note="No issues detected" />
        <Stat icon={<TriangleAlert size={22} className="text-rose-500" />} label="Missing values" value="2%" note="1,180 customers" />
      </div>

      <div className="mt-6 rounded-xl bg-white dark:bg-gray-900 p-4">
        <p className="text-base text-gray-900 dark:text-gray-100 mb-3">Sample Data</p>
        {customersLoading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
            <Loader2 size={16} className="animate-spin" /> Loading sample customers…
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-base">
              <thead>
                <tr className="text-left text-gray-900 dark:text-gray-100">
                  {["Contact", "Contact ID", "Source Value", "Trait Value", "Last Updated"].map((h) => (
                    <th key={h} className="px-4 py-4 font-normal">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const display = sampleValueFor(c, config.name, fallbackValue);
                  return (
                    <tr key={c.customer_id} className="border-t border-gray-100 dark:border-gray-800">
                      <td className="px-4 py-4">
                        <span className="flex items-center gap-3">
                          <span className="w-9 h-9 rounded-full bg-indigo-100 dark:bg-indigo-500/20 text-indigo-500 flex items-center justify-center shrink-0">
                            <User size={20} />
                          </span>
                          <span className="block text-gray-900 dark:text-gray-100">{c.name}</span>
                        </span>
                      </td>
                      <td className="px-4 py-4 text-gray-900 dark:text-gray-100">{c.display_id}</td>
                      <td className="px-4 py-4 text-gray-900 dark:text-gray-100">{display}</td>
                      <td className="px-4 py-4 text-gray-900 dark:text-gray-100">{display}</td>
                      <td className="px-4 py-4 text-gray-900 dark:text-gray-100 whitespace-nowrap">{formatProfileTimestamp(c.latest_event_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2 mt-6">
        <button type="button" className={ghostBtn} onClick={onBack}>
          Back to Configuration
        </button>
        <button type="button" className={primaryBtn} onClick={onSave}>
          Save
        </button>
      </div>
    </div>
  );
};

export default StepPreview;
